import { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout, { toast } from './IndustrialLayout';
import { isAdmin } from '../../utils/entitlements';
import { makeSimpleRing } from '../../utils/polygon';

// ── Object Intelligence (AI Object Intelligence spec) ─────────────────────────
// One canvas, three modes on the live camera:
//   counting → draw a LINE, count crossings with direction (attendance report)
//   zone     → draw a POLYGON, alert when a person stands inside (restricted zone)
//   boundary → draw a LINE, alert when a person crosses toward the guarded side
// Zone/boundary fire real alerts → snapshot + clip + Telegram + siren for free.

const MODES = [
  { v: 'counting', label: 'Object Counting', detector: 'object_count',   shape: 'line' },
  { v: 'zone',     label: 'Restricted Zone',  detector: 'zone_intrusion', shape: 'poly' },
  { v: 'boundary', label: 'Boundary Line',    detector: 'zone_intrusion', shape: 'line' },
  { v: 'ignore',   label: 'Ignore Zone',      detector: null,             shape: 'poly' },
];

// The built-in base model is a full COCO detector — the operator can count any of
// these classes, not just people (same model, just a different class filter). Index
// = COCO class id, which is exactly what the backend counts on. Kept as a dropdown
// (was a free-text box) so nobody has to guess the class name/id.
const COCO_CLASSES = [
  'person', 'bicycle', 'car', 'motorcycle', 'airplane', 'bus', 'train', 'truck',
  'boat', 'traffic light', 'fire hydrant', 'stop sign', 'parking meter', 'bench',
  'bird', 'cat', 'dog', 'horse', 'sheep', 'cow', 'elephant', 'bear', 'zebra',
  'giraffe', 'backpack', 'umbrella', 'handbag', 'tie', 'suitcase', 'frisbee',
  'skis', 'snowboard', 'sports ball', 'kite', 'baseball bat', 'baseball glove',
  'skateboard', 'surfboard', 'tennis racket', 'bottle', 'wine glass', 'cup', 'fork',
  'knife', 'spoon', 'bowl', 'banana', 'apple', 'sandwich', 'orange', 'broccoli',
  'carrot', 'hot dog', 'pizza', 'donut', 'cake', 'chair', 'couch', 'potted plant',
  'bed', 'dining table', 'toilet', 'tv', 'laptop', 'mouse', 'remote', 'keyboard',
  'cell phone', 'microwave', 'oven', 'toaster', 'sink', 'refrigerator', 'book',
  'clock', 'vase', 'scissors', 'teddy bear', 'hair drier', 'toothbrush',
];

export default function IndustrialObjectCountPage() {
  const [cameras, setCameras] = useState([]);
  const [models, setModels]   = useState([]);
  const [knownNames, setKnownNames] = useState([]);  // enrolled persons for the Authorised combo
  const [scope, setScope]     = useState(null);
  const [mode, setMode]       = useState('counting');
  const [cfg, setCfg]         = useState(defaults('counting'));
  const [enabled, setEnabled] = useState([]);
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);   // counting: which line is being edited
  const [drawMode, setDrawMode]   = useState('line'); // counting: draw the 'line' or a 'staff' zone
  const [retentionDays, setRetentionDays] = useState(30); // how long count evidence is kept

  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgErr, setImgErr] = useState(false); // frame failed to load (camera offline / backend down)
  const [pts, setPts]       = useState([]);   // in-progress normalized points
  const [live, setLive]     = useState(false);// live annotated preview (boxes + tally)

  // purpose: 'object' = throughput (boxes, vehicles) → Count Report.
  //          'footfall' = customers in/out of a shop → Footfall Report, and the backend
  //          applies the people-specific rules (staff zones, longer track memory,
  //          crossing debounce, empty-area occupancy reset).
  function newCountLine(i = 0) {
    return { name: `Line ${i + 1}`, class_ids: [0], object: 'person', line: null,
             invert: false, min_area_frac: 0.0, max_area_frac: 1.0,
             purpose: 'object', staff_zones: [] };
  }
  // A line can count SEVERAL classes → class_ids[]. Back-compat: single class_id → [id].
  function normLine(l, i) {
    const base = newCountLine(i);
    const cids = Array.isArray(l.class_ids) ? l.class_ids
      : (l.class_id != null ? [l.class_id] : base.class_ids);
    return { ...base, ...l, class_ids: cids,
             purpose: l.purpose || base.purpose,
             staff_zones: Array.isArray(l.staff_zones) ? l.staff_zones : [] };
  }
  // Normalise any saved object_count shape (multi-line, legacy single-line, or empty)
  // into the multi-line editor shape: { model, model_path, conf, lines:[…] }.
  function normalizeCounting(saved) {
    // match_dist_px / segment_margin: null = backend default (auto distance, check off);
    // a null that reaches the save clears the key on the camera.
    const cam = { model: saved?.model || 'person', model_path: saved?.model_path || '', conf: saved?.conf ?? 0.35, imgsz: saved?.imgsz ?? 640,
                  match_dist_px: saved?.match_dist_px ?? null, segment_margin: saved?.segment_margin ?? null,
                  count_fps: saved?.count_fps ?? null };
    if (saved && Array.isArray(saved.lines) && saved.lines.length)
      return { ...cam, lines: saved.lines.map(normLine) };
    if (saved && saved.line)   // legacy single-line → one line
      return { ...cam, lines: [normLine({
        name: saved.name || saved.object || 'Line 1', class_id: saved.class_id ?? null,
        object: saved.object || 'person', line: saved.line, invert: !!saved.invert,
        min_area_frac: saved.min_area_frac ?? 0, max_area_frac: saved.max_area_frac ?? 1 }, 0)] };
    return { ...cam, lines: [newCountLine(0)] };
  }

  // Ignore zones live in cam_config.exclusion_zones (a list). Normalise to {zones:[poly,…]},
  // converting any legacy rectangle zones {x1,y1,x2,y2} to a 4-corner polygon.
  function normalizeIgnore(cam) {
    const raw = cam?.cam_config?.exclusion_zones || [];
    const zones = raw.map(z => {
      if (Array.isArray(z?.polygon) && z.polygon.length >= 3) return z.polygon;
      if (z && z.x1 != null) return [[z.x1, z.y1], [z.x2, z.y1], [z.x2, z.y2], [z.x1, z.y2]];
      return null;
    }).filter(Boolean);
    return { zones };
  }

  function defaults(m) {
    if (m === 'counting')
      return { model: 'person', model_path: '', conf: 0.35, imgsz: 640, lines: [newCountLine(0)] };
    if (m === 'ignore')
      return { zones: [] };
    if (m === 'zone')
      return { mode: 'zone', object: 'person', polygon: null, authorized_names: [],
               persist_frames: 3, cooldown_secs: 20, dwell_secs: 0 };
    return { mode: 'boundary', object: 'person', line: null, invert: false,
             authorized_names: [], cooldown_secs: 20 };
  }

  // Counting normally draws a LINE, but the same canvas also draws STAFF ZONES for a
  // footfall line — polygons over the seats behind the counter. A person whose track
  // starts inside one is staff and never counts, however far they later walk. They are
  // deliberately NOT ignore zones: ignoring those detections would make a staff member
  // appear as a brand-new track the moment they step out, and count as a customer.
  const staffDraw = mode === 'counting' && drawMode === 'staff';
  const shape = staffDraw ? 'poly' : MODES.find(m => m.v === mode).shape;

  // A fresh frame URL gets a fresh chance to load — clear any prior error.
  useEffect(() => { if (imgUrl) setImgErr(false); }, [imgUrl]);

  // ── initial load ──
  useEffect(() => {
    Promise.all([
      apiFetch('/ui/cameras').then(r => r.json()).catch(() => ({ cameras: [] })),
      apiFetch('/ui/models').then(r => r.json()).catch(() => ({ models: [] })),
    ]).then(([cams, mdl]) => {
      const list = cams.cameras || [];
      setCameras(list); setModels(mdl.models || []);
      if (list.length) selectCamera(list[0].idx, list, 'counting');
    });
    apiFetch('/ui/count/settings').then(r => r.json())
      .then(d => setRetentionDays(d.retention_days || 30)).catch(() => {});
    // Enrolled persons for the Authorised (ignore) combo — big page to get them all.
    apiFetch('/ui/faces/known?per_page=1000')
      .then(r => r.json())
      .then(d => setKnownNames((d.persons || []).map(p => p.name || p).filter(Boolean).sort()))
      .catch(() => {});
  }, []);  // eslint-disable-line

  const selectCamera = useCallback((idx, camList, m) => {
    const cams = camList || cameras;
    const cam  = cams.find(c => c.idx === idx);
    const useMode = m || mode;
    const dc  = cam?.detector_config || {};
    const saved = useMode === 'counting' ? dc.object_count : dc.zone_intrusion;
    setScope(idx); setEnabled([...(cam?.detectors || [])]);
    setCfg(useMode === 'counting' ? normalizeCounting(saved)
      : useMode === 'ignore'      ? normalizeIgnore(cam)
      :                             { ...defaults(useMode), ...(saved || {}) });
    setActiveIdx(0); setPts([]); setDirty(false); setDrawMode('line');
    apiImageUrl(`/cameras/${idx}/frame`).then(u => setImgUrl(u)).catch(() => setImgUrl(null));
  }, [cameras, mode]);

  const switchMode = (m) => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setMode(m); setDrawMode('line');
    const cam = cameras.find(c => c.idx === scope);
    const dc  = cam?.detector_config || {};
    const saved = m === 'counting' ? dc.object_count : dc.zone_intrusion;
    if (m === 'counting') { setCfg(normalizeCounting(saved)); setActiveIdx(0); setPts([]); setDirty(false); return; }
    if (m === 'ignore')   { setCfg(normalizeIgnore(cam));     setActiveIdx(0); setPts([]); setDirty(false); return; }
    // keep saved config only if its shape matches this mode
    const base = defaults(m);
    setCfg(saved && (m === 'zone' ? saved.mode !== 'boundary' : m === 'boundary' ? saved.mode === 'boundary' : true)
      ? { ...base, ...saved } : base);
    setPts([]); setDirty(false);
  };

  // ── redraw ──
  useEffect(() => {
    const cv = canvasRef.current, im = imgRef.current;
    if (!cv || !im || !im.complete) return;
    const W = cv.width, H = cv.height, ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const dot = (p, color) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(p[0] * W, p[1] * H, 6, 0, 7); ctx.fill(); };
    const drawLine = (ln, color, lw) => {
      ctx.strokeStyle = color; ctx.lineWidth = lw;
      ctx.beginPath(); ctx.moveTo(ln[0][0] * W, ln[0][1] * H); ctx.lineTo(ln[1][0] * W, ln[1][1] * H); ctx.stroke();
      ln.forEach(p => dot(p, color));
    };
    // A / B endpoint tags + an IN-direction arrow. The arrow points to the side a
    // crossing counts as IN — same geometry the backend counter uses (perp sign),
    // flipped by `invert`. Makes "A → B = IN" concrete: whatever way the arrow points
    // is IN; the reverse is OUT (e.g. box returned to inventory).
    const drawDir = (ln, invert, color) => {
      const ax = ln[0][0] * W, ay = ln[0][1] * H, bx = ln[1][0] * W, by = ln[1][1] * H;
      ctx.fillStyle = color; ctx.font = 'bold 11px sans-serif';
      ctx.fillText('A', ax + 8, ay + 4); ctx.fillText('B', bx + 8, by + 4);
      let nx = -(by - ay), ny = (bx - ax);           // toward +perp side = IN (not inverted)
      const len = Math.hypot(nx, ny) || 1; nx /= len; ny /= len;
      if (invert) { nx = -nx; ny = -ny; }
      const mx = (ax + bx) / 2, my = (ay + by) / 2, L = 34;
      const tx = mx + nx * L, ty = my + ny * L;
      ctx.strokeStyle = color; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(mx, my); ctx.lineTo(tx, ty); ctx.stroke();
      const ah = 9, ang = Math.atan2(ty - my, tx - mx);
      ctx.beginPath(); ctx.moveTo(tx, ty);
      ctx.lineTo(tx - ah * Math.cos(ang - 0.4), ty - ah * Math.sin(ang - 0.4));
      ctx.lineTo(tx - ah * Math.cos(ang + 0.4), ty - ah * Math.sin(ang + 0.4));
      ctx.closePath(); ctx.fill();
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText('IN', tx + nx * 10 - 6, ty + ny * 10 + 4);
    };
    // counting: draw ALL lines (active green, others blue, labelled)
    if (mode === 'counting') {
      // Staff zones of the ACTIVE line, under the lines so the line stays readable.
      ((cfg.lines || [])[activeIdx]?.staff_zones || []).forEach((poly, i) => {
        if (!poly || poly.length < 3) return;
        ctx.strokeStyle = '#a855f7'; ctx.fillStyle = 'rgba(168,85,247,0.22)'; ctx.lineWidth = 2;
        ctx.beginPath(); poly.forEach((p, j) => j ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
        ctx.closePath(); ctx.fill(); ctx.stroke();
        poly.forEach(p => dot(p, '#a855f7'));
        ctx.fillStyle = '#d8b4fe'; ctx.font = 'bold 12px sans-serif';
        ctx.fillText(`👤 Staff ${i + 1}`, poly[0][0] * W + 4, poly[0][1] * H - 4);
      });
      (cfg.lines || []).forEach((L, i) => {
        if (!L.line) return;
        const active = i === activeIdx;
        const col = active ? '#22c55e' : '#3b82f6';
        drawLine(L.line, col, active ? 3 : 2);
        ctx.fillStyle = col; ctx.font = 'bold 12px sans-serif';
        ctx.fillText(L.name || `Line ${i + 1}`, L.line[0][0] * W + 6, L.line[0][1] * H - 6);
        drawDir(L.line, L.invert, col);
      });
    } else if (mode === 'ignore') {
      // draw every ignore polygon (grey, labelled) — detections here raise no alert
      (cfg.zones || []).forEach((poly, i) => {
        if (!poly || poly.length < 3) return;
        ctx.strokeStyle = '#94a3b8'; ctx.fillStyle = 'rgba(148,163,184,0.28)'; ctx.lineWidth = 2;
        ctx.beginPath(); poly.forEach((p, j) => j ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
        ctx.closePath(); ctx.fill(); ctx.stroke();
        poly.forEach(p => dot(p, '#94a3b8'));
        ctx.fillStyle = '#cbd5e1'; ctx.font = 'bold 12px sans-serif';
        ctx.fillText(`🚫 Ignore ${i + 1}`, poly[0][0] * W + 4, poly[0][1] * H - 4);
      });
    } else if (shape === 'line' && cfg.line) {
      drawLine(cfg.line, mode === 'boundary' ? '#ef4444' : '#22c55e', 3);
    }
    if (mode === 'zone' && cfg.polygon && cfg.polygon.length >= 3) {
      ctx.strokeStyle = '#ef4444'; ctx.fillStyle = 'rgba(239,68,68,0.15)'; ctx.lineWidth = 2;
      ctx.beginPath(); cfg.polygon.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      cfg.polygon.forEach(p => dot(p, '#ef4444'));
    }
    // in-progress points
    if (pts.length) {
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2; ctx.beginPath();
      pts.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H)); ctx.stroke();
      // A zone is a RING. Drawn open, four points showed three segments and read
      // as a triangle, and crossed edges were invisible until after saving.
      if (shape === 'poly' && pts.length >= 3) {
        ctx.save();
        ctx.setLineDash([5, 4]); ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pts[pts.length - 1][0] * W, pts[pts.length - 1][1] * H);
        ctx.lineTo(pts[0][0] * W, pts[0][1] * H);
        ctx.stroke();
        ctx.restore();
      }
      pts.forEach((p, i) => {
        dot(p, '#f59e0b');
        if (shape !== 'poly') return;
        ctx.fillStyle = '#1f2937'; ctx.font = 'bold 10px sans-serif';
        ctx.fillText(String(i + 1), p[0] * W - 3, p[1] * H + 3.5);   // click ORDER
      });
    }
  }, [imgUrl, cfg, pts, shape, mode, activeIdx]);

  const onCanvasClick = (e) => {
    const cv = canvasRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const p = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    if (shape === 'poly') { setPts(prev => [...prev, p]); return; }   // functional: no dropped clicks
    const next = [...pts, p];
    if (next.length === 2) { mode === 'counting' ? patchLine('line', next) : patch('line', next); setPts([]); }
    else setPts(next);
  };
  const finishZone = () => {
    if (pts.length < 3) { toast('Click at least 3 points.', 'err'); return; }
    // Corners clicked diagonally cross into a bowtie, and ray-casting membership
    // then tests the wrong area. Straighten it and say so.
    const { poly, fixed } = makeSimpleRing(pts);
    if (fixed) toast('Those corners crossed over — reordered them into a clean shape.', 'warn');
    if (staffDraw) { patchLine('staff_zones', [...(AL.staff_zones || []), poly]); setPts([]); }
    else if (mode === 'ignore') { setCfg(c => ({ ...c, zones: [...(c.zones || []), poly] })); setDirty(true); setPts([]); }
    else { patch('polygon', poly); setPts([]); }
  };
  const saveRetention = (v) => {
    const days = Math.max(1, Number(v) || 30);
    setRetentionDays(days);
    apiFetch('/ui/count/settings', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ retention_days: days }) }).catch(() => {});
  };
  const removeStaffZone = (i) =>
    patchLine('staff_zones', (AL.staff_zones || []).filter((_, j) => j !== i));
  const removeIgnoreZone = (i) => { setCfg(c => ({ ...c, zones: (c.zones || []).filter((_, j) => j !== i) })); setDirty(true); };

  const patch = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setDirty(true); };
  // Counting line helpers — edit / add / remove the active line.
  const AL = (cfg.lines && cfg.lines[activeIdx]) || {};
  const patchLine = (k, v) => {
    setCfg(c => ({ ...c, lines: (c.lines || []).map((L, i) => i === activeIdx ? { ...L, [k]: v } : L) }));
    setDirty(true);
  };
  const addLine = () => {
    setCfg(c => { const lines = [...(c.lines || []), newCountLine((c.lines || []).length)]; setActiveIdx(lines.length - 1); return { ...c, lines }; });
    setDirty(true);
  };
  const removeLine = (i) => {
    setCfg(c => {
      const lines = (c.lines || []).filter((_, j) => j !== i);
      const next = lines.length ? lines : [newCountLine(0)];
      setActiveIdx(Math.max(0, Math.min(activeIdx, next.length - 1)));
      return { ...c, lines: next };
    });
    setDirty(true);
  };

  const chosen = models.find(m => m.path === cfg.model_path);
  const pickModel = (val) => {
    setCfg(c => {
      const isP = val === 'person';
      const m = isP ? null : models.find(x => x.path === val);
      const cids = isP ? [0] : (m?.classes?.[0]?.id != null ? [m.classes[0].id] : []);
      const obj  = isP ? 'person' : (m?.classes?.[0]?.name || 'object');
      return {
        ...c, model: isP ? 'person' : (m?.name || 'custom'), model_path: isP ? '' : val,
        lines: (c.lines || []).map((L, i) => i === activeIdx ? { ...L, class_ids: cids, object: obj } : L),
      };
    });
    setDirty(true);
  };
  // Toggle a class id in the active line's class_ids, and derive the object label.
  const classNameOf = (id) => (chosen ? chosen.classes.find(c => c.id === id)?.name : COCO_CLASSES[id]) || String(id);
  const toggleClass = (id) => {
    const cur = AL.class_ids || [];
    const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    patchLine('class_ids', next);
    patchLine('object', next.length ? next.map(classNameOf).join(' / ') : 'object');
  };

  const save = async () => {
    if (scope == null) return;
    const md = MODES.find(m => m.v === mode);

    // Ignore Zone → writes cam_config.exclusion_zones (not a detector). Auto-commit any
    // in-progress polygon too. The person detector drops detections inside these.
    if (mode === 'ignore') {
      const zones = [...(cfg.zones || [])];
      if (pts.length >= 3) zones.push(makeSimpleRing(pts).poly);
      setSaving(true);
      try {
        const body = { cam_config: { exclusion_zones: zones.map(poly => ({ polygon: poly })) } };
        const r = await apiFetch(`/cameras/${scope}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (!r.ok) throw new Error();
        setCfg({ zones }); setPts([]); setDirty(false);
        toast(`Saved — ${zones.length} ignore zone${zones.length === 1 ? '' : 's'} on this camera.`, 'ok');
      } catch { toast('Save failed.', 'err'); }
      finally { setSaving(false); }
      return;
    }

    // Auto-commit in-progress points so users don't have to click "Finish zone" /
    // a second line-point before saving (the drawn-but-uncommitted trap).
    const effPolygon = cfg.polygon || (pts.length >= 3 ? makeSimpleRing(pts).poly : null);
    const effLine    = cfg.line    || (pts.length === 2 ? pts : null);
    if (mode === 'counting' && !(cfg.lines || []).some(L => L.line)) { toast('Draw at least one line.', 'err'); return; }
    if (mode === 'zone'     && !(effPolygon && effPolygon.length >= 3)) { toast('Draw the zone (3+ points) first.', 'err'); return; }
    if (mode === 'boundary' && !effLine) { toast('Draw the boundary line first.', 'err'); return; }
    setSaving(true);
    try {
      const dets = enabled.includes(md.detector) ? enabled : [...enabled, md.detector];
      const key  = md.detector === 'object_count' ? 'object_count' : 'zone_intrusion';
      const payload = mode === 'counting' ? { ...cfg, lines: (cfg.lines || []).filter(L => L.line) }
        : mode === 'zone'                 ? { ...cfg, polygon: effPolygon }
        :                                   { ...cfg, line: effLine };
      const body = { detectors: dets, detector_config: { [key]: payload } };
      const r = await apiFetch(`/cameras/${scope}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      // reflect the committed shape so the canvas/state match what was saved
      if (mode === 'zone') setCfg(c => ({ ...c, polygon: effPolygon }));
      if (mode === 'boundary') setCfg(c => ({ ...c, line: effLine }));
      setPts([]); setEnabled(dets); setDirty(false);
      toast(`Saved — ${md.label} active on this camera.`, 'ok');
    } catch { toast('Save failed.', 'err'); }
    finally { setSaving(false); }
  };

  // Live annotated preview — poll the server-drawn frame (detection boxes + shape + tally).
  useEffect(() => {
    if (!live || scope == null) return;
    let prev = null, alive = true;
    const tick = () => apiImageUrl(`/ui/cameras/${scope}/annotated?t=${Date.now()}`).then(u => {
      if (!alive) { URL.revokeObjectURL(u); return; }
      setImgUrl(u); if (prev) URL.revokeObjectURL(prev); prev = u;
    }).catch(() => {});
    tick(); const iv = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(iv); if (prev) URL.revokeObjectURL(prev); };
  }, [live, scope]);

  const test = async () => {
    if (mode === 'counting') {
      try {
        const r = await apiFetch(`/ui/count/test?cam=${scope}&direction=in`, { method: 'POST' });
        const d = await r.json();
        toast(`Test crossing logged — IN ${d.in} / OUT ${d.out}. See the Count Report menu.`, 'ok');
      } catch { toast('Test failed.', 'err'); }
    } else {
      setLive(true);
      toast('Live preview on — walk a person through; the alert appears in Ops Console.', 'warn');
    }
  };

  const camName = cameras.find(c => c.idx === scope)?.name || (scope != null ? `Camera ${scope}` : '');

  return (
    <IndustrialLayout eyebrow="Object intelligence" title="Object Intelligence Config"
      subtitle={`${cameras.length} CAMERAS · ${models.length} CUSTOM MODELS`}>
      {isAdmin() && <ModelUpload onUploaded={() => apiFetch('/ui/models').then(r => r.json()).then(d => setModels(d.models || []))} />}

          <div className="ind-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <span className="ind-eye">Camera</span>
              <select value={scope ?? ''} onChange={e => { if (dirty && !window.confirm('Discard unsaved changes?')) return; selectCamera(Number(e.target.value)); }} style={sel}>
                {cameras.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name || `Camera ${c.idx}`}</option>)}
              </select>
              <span className="ind-eye" style={{ marginLeft: 12 }}>Mode</span>
              <div className="ind-pills">
                {MODES.map(m => <button key={m.v} className={`ind-pill${mode === m.v ? ' on' : ''}`} onClick={() => switchMode(m.v)}>{m.label}</button>)}
              </div>
            </div>

            <div style={{ position: 'relative', width: '100%', maxWidth: 720, minHeight: 280, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', display: 'grid', placeItems: 'center' }}>
              {imgUrl && !imgErr
                ? <img ref={imgRef} src={imgUrl} alt="" onError={() => setImgErr(true)}
                    onLoad={() => { const cv = canvasRef.current, im = imgRef.current; if (cv && im) { cv.width = im.clientWidth; cv.height = im.clientHeight; setPts(p => [...p]); } }}
                    style={{ width: '100%', display: 'block' }} />
                : <div className="ind-empty" style={{ padding: 24, textAlign: 'center', maxWidth: 420 }}>
                    {imgErr
                      ? <>📷 Couldn’t load a frame from <b>{camName}</b>.<br />The camera may be offline, or the backend patch isn’t deployed yet.<br />Try <b>↻ Refresh frame</b>, or pick another camera.</>
                      : <>📷 Camera frame appears here.<br />Selecting a camera loads a still to draw on — or press <b>↻ Refresh frame</b>.</>}
                  </div>}
              {imgUrl && !imgErr &&
                <canvas ref={canvasRef} onClick={onCanvasClick} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'crosshair' }} />}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                {staffDraw
                  ? `Click the corners of the STAFF area, then Finish zone. ${(AL.staff_zones || []).length} staff zone(s) on "${AL.name}".`
                  : mode === 'counting'
                  ? (AL.line ? `✅ "${AL.name}" set — click 2 new points to redraw it.` : `Drawing "${AL.name}": click point A then point B.`)
                  : mode === 'ignore'
                    ? `Click the corners of an area to IGNORE, then Finish zone. ${(cfg.zones || []).length} zone(s) set.`
                    : shape === 'poly'
                      ? (cfg.polygon ? '✅ Zone set — click new points then Finish to redraw.' : 'Click the corners of the protected zone, then Finish zone.')
                      : (cfg.line ? '✅ Line set — click 2 new points to redraw.' : 'Click point A then point B to draw the line.')}
              </span>
              {shape === 'poly' && pts.length > 0 && <button className="ind-pill" onClick={finishZone}>Finish zone ({pts.length})</button>}
              {mode === 'zone' && cfg.polygon && <button className="ind-pill" onClick={() => { patch('polygon', null); setPts([]); }}>Clear zone</button>}
              {mode === 'counting' && AL.line && <button className="ind-pill" onClick={() => { patchLine('line', null); setPts([]); }}>Clear “{AL.name}”</button>}
              {mode === 'boundary' && cfg.line && <button className="ind-pill" onClick={() => { patch('line', null); setPts([]); }}>Clear line</button>}
              {pts.length > 0 && <button className="ind-pill" onClick={() => setPts([])}>Cancel</button>}
              <button className={`ind-pill${live ? ' on' : ''}`} onClick={() => { const n = !live; setLive(n); if (!n) apiImageUrl(`/cameras/${scope}/frame`).then(u => setImgUrl(u)); }}>
                {live ? '⏸ Live preview' : '▶ Live preview'}
              </button>
              <button className="ind-pill" onClick={test}>🧪 Test</button>
              {!live && <button className="ind-pill" onClick={() => apiImageUrl(`/cameras/${scope}/frame`).then(u => setImgUrl(u))}>↻ Refresh frame</button>}
            </div>

            {/* mode-specific config */}
            <div className="ind-dgrid ind-dgrid-fit" style={{ marginTop: 14 }}>
              {mode === 'counting' && <>
                {/* Camera-level: one model + confidence shared by every line */}
                <Field label="AI Model">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <select value={cfg.model_path || 'person'} onChange={e => pickModel(e.target.value)} style={sel}>
                      <option value="person">Person (built-in base model)</option>
                      {models.map(m => <option key={m.path} value={m.path}>{m.name}</option>)}
                    </select>
                    <span style={{ fontSize: 11, color: 'var(--ind-tx3)', maxWidth: 260 }}>
                      {chosen
                        ? (chosen.description || `Custom model · ${chosen.classes?.length || 0} classes`)
                        : 'Built-in COCO detector — each line below picks its own class (person, car, truck…).'}
                    </span>
                  </div>
                </Field>
                <Range label="Confidence" val={cfg.conf} set={v => patch('conf', v)} min={0.1} max={0.9} step={0.05} />
                <Field label="Detection resolution">
                  <select value={cfg.imgsz ?? 640} onChange={e => patch('imgsz', Number(e.target.value))} style={sel}>
                    <option value={640}>640 · fast (near/large objects)</option>
                    <option value={960}>960 · better for distant objects</option>
                    <option value={1280}>1280 · best for far/small (heavier CPU)</option>
                  </select>
                  <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4 }}>
                    Raise this if far cars on the road aren’t detected (line high in the frame). Higher = more CPU.
                  </span>
                </Field>
                <Field label="Tracking distance (px)">
                  <input type="number" min={50} max={2000} step={10} placeholder="Auto"
                         value={cfg.match_dist_px ?? ''} style={{ ...inp, width: 110 }}
                         onChange={e => patch('match_dist_px', e.target.value === '' ? null : Math.round(Number(e.target.value)))} />
                  <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 260 }}>
                    How far one person may move between two detections and still be the same track.
                    Auto = ⅓ of frame width. Lower it (e.g. 250) if groups get miscounted.
                  </span>
                </Field>
                <Field label="Count crossings">
                  <select value={cfg.segment_margin == null ? '' : String(cfg.segment_margin)} style={sel}
                          onChange={e => patch('segment_margin', e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">Anywhere along the line’s extension (default)</option>
                    <option value="0.15">Only on the drawn line</option>
                    {cfg.segment_margin != null && Number(cfg.segment_margin) !== 0.15 &&
                      <option value={String(cfg.segment_margin)}>Only on the drawn line (±{Math.round(cfg.segment_margin * 100)}%)</option>}
                  </select>
                  <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 260 }}>
                    “Only on the drawn line” ignores people passing beyond the line’s ends — draw it across the full gate.
                  </span>
                </Field>
                <Field label="People counting speed">
                  <select value={cfg.count_fps ? String(cfg.count_fps) : ''} style={sel}
                          onChange={e => patch('count_fps', e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">With the camera’s detection (default)</option>
                    <option value="2">Separate, 2 checks per second</option>
                    {cfg.count_fps && Number(cfg.count_fps) !== 2 &&
                      <option value={String(cfg.count_fps)}>Separate, {cfg.count_fps} per second</option>}
                  </select>
                  <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 260 }}>
                    On a busy camera people can cross between two detections and go uncounted. “Separate” checks
                    person lines twice a second on their own, at a small extra CPU cost. Vehicle lines are not affected.
                  </span>
                </Field>

                {/* Counting lines — add several to count different things on one camera */}
                <Field label="Counting lines">
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(cfg.lines || []).map((L, i) => (
                      <button key={i} className={`ind-pill${i === activeIdx ? ' on' : ''}`} onClick={() => { setActiveIdx(i); setPts([]); }}>
                        {L.line ? '✅' : '✏️'} {L.name || `Line ${i + 1}`}
                      </button>
                    ))}
                    <button className="ind-pill" onClick={addLine}>＋ Add line</button>
                    {(cfg.lines || []).length > 1 &&
                      <button className="ind-pill" onClick={() => removeLine(activeIdx)} style={{ color: 'var(--ind-bad, #e74c3c)' }}>🗑 Remove</button>}
                  </div>
                </Field>

                {/* Selected line settings */}
                <Field label="Line name">
                  <input value={AL.name || ''} onChange={e => patchLine('name', e.target.value)} style={inp} placeholder="e.g. Guards, Cars" />
                </Field>
                <Field label="What is this line for?">
                  <select value={AL.purpose || 'object'} onChange={e => {
                    const v = e.target.value;
                    patchLine('purpose', v);
                    // Footfall counts people, full stop — pin the class so the line can't
                    // be pointed at boxes while claiming to count customers.
                    if (v === 'footfall') { patchLine('class_ids', [0]); patchLine('object', 'person'); }
                    // Vehicles default to car / motorcycle / bus / truck; classes stay editable.
                    if (v === 'vehicle') { patchLine('class_ids', [2, 3, 5, 7]); patchLine('object', 'vehicle'); }
                  }} style={sel}>
                    <option value="object">Object throughput — boxes (Count Report)</option>
                    <option value="footfall">People in / out — customers (Footfall Report)</option>
                    <option value="vehicle">Vehicles in / out — with number plates (Vehicle Report)</option>
                  </select>
                  <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 300 }}>
                    {AL.purpose === 'footfall'
                      ? 'Counts persons only. Staff zones below are excluded, repeat crossings are debounced, and occupancy resets to zero whenever the area is empty.'
                      : AL.purpose === 'vehicle'
                        ? 'Counts vehicles by type and reads each one’s number plate from the main stream while it is in view.'
                        : 'Plain crossing tally for the object classes chosen above.'}
                  </span>
                </Field>
                {AL.purpose === 'vehicle' && (
                  <Field label="Number plates">
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                      <input type="checkbox" checked={AL.read_plates !== false}
                        onChange={e => patchLine('read_plates', e.target.checked)} />
                      Read number plates
                    </label>
                    <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 300 }}>
                      Works best where vehicles pass slowly, facing the camera, with the plate at least
                      ~50&nbsp;px wide on the live view. At night, headlight and IR glare often wash plates
                      out — those crossings still count and keep the plate photo, just without text.
                    </span>
                  </Field>
                )}
                {AL.purpose === 'footfall' && (
                  <Field label="Staff zones">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 260 }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button className={`ind-pill${drawMode === 'line' ? ' on' : ''}`}
                          onClick={() => { setDrawMode('line'); setPts([]); }}>✏️ Draw line</button>
                        <button className={`ind-pill${drawMode === 'staff' ? ' on' : ''}`}
                          onClick={() => { setDrawMode('staff'); setPts([]); }}>👤 Draw staff zone</button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(AL.staff_zones || []).map((_z, i) => (
                          <span key={i} className="ind-pill" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            Staff {i + 1}
                            <button onClick={() => removeStaffZone(i)}
                              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                          </span>
                        ))}
                        {(AL.staff_zones || []).length === 0 &&
                          <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>None — every person crossing counts.</span>}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--ind-tx3)', maxWidth: 300 }}>
                        Draw over the seats behind the counter. Anyone whose track <b>starts</b> in a staff
                        zone never counts, even after they walk away from it.
                      </span>
                    </div>
                  </Field>
                )}
                {AL.purpose === 'footfall' && (
                  <Field label="Occupancy">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 300 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        <input type="checkbox" checked={AL.show_occupancy !== false}
                          onChange={e => patchLine('show_occupancy', e.target.checked)} />
                        Show “Inside now” and “Peak” on the Footfall Report
                      </label>
                      <span style={{ fontSize: 11, color: 'var(--ind-tx3)' }}>
                        Only meaningful when the area behind the line is in view (a shop floor). For a gate
                        into somewhere the camera cannot see — turnstiles into an office — untick it; the
                        report then shows In and Out only.
                      </span>
                    </div>
                  </Field>
                )}
                <Field label="Object / classes">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(AL.class_ids || []).map(id => (
                        <span key={id} className="ind-pill" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          {classNameOf(id)}
                          <button onClick={() => toggleClass(id)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                        </span>
                      ))}
                      {(AL.class_ids || []).length === 0 && <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>Pick at least one class.</span>}
                    </div>
                    <select value="" style={sel} onChange={e => { if (e.target.value !== '') toggleClass(Number(e.target.value)); }}>
                      <option value="">＋ Add class…</option>
                      {(chosen ? chosen.classes.map(c => ({ id: c.id, name: c.name })) : COCO_CLASSES.map((n, i) => ({ id: i, name: n })))
                        .filter(o => !(AL.class_ids || []).includes(o.id))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                </Field>
                <Field label="Direction (IN side)">
                  <select value={AL.invert ? '1' : '0'} onChange={e => patchLine('invert', e.target.value === '1')} style={sel}>
                    <option value="0">IN = the ➤ arrow side (A → B)</option>
                    <option value="1">IN = the other side (flip arrow)</option>
                  </select>
                  <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 260 }}>
                    The green <b>IN</b> arrow on the preview shows which way counts as IN. Your delivery line is
                    fixed — set the arrow to your normal flow; a crossing the other way (OUT) then means the box
                    came back to inventory. Flip this if it reads backwards.
                  </span>
                </Field>
                <Range label="Min size" val={AL.min_area_frac ?? 0} set={v => patchLine('min_area_frac', v)} min={0} max={0.2} step={0.005} />
                <Range label="Max size" val={AL.max_area_frac ?? 1} set={v => patchLine('max_area_frac', v)} min={0.05} max={1} step={0.05} />
                {/* Retention is a setting, so it lives here with the other settings —
                    it used to sit in the report's filter row, which is not a filter. */}
                {isAdmin() && (
                  <Field label="Keep evidence">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" min={1} max={365} value={retentionDays}
                        onChange={e => saveRetention(e.target.value)}
                        style={{ ...inp, width: 70, textAlign: 'center' }} />
                      <span style={{ fontSize: 12, color: 'var(--ind-tx2)' }}>days</span>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--ind-tx3)', display: 'block', marginTop: 4, maxWidth: 260 }}>
                      How long crossing events and their snapshots are kept before purging.
                      Applies to every counting camera.
                    </span>
                  </Field>
                )}
              </>}

              {(mode === 'zone' || mode === 'boundary') && <>
                <Field label="Object label"><input value={cfg.object} onChange={e => patch('object', e.target.value)} style={inp} /></Field>
                <Field label="Authorised (ignore)">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(cfg.authorized_names || []).map(n => (
                        <span key={n} className="ind-pill" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          {n}
                          <button onClick={() => patch('authorized_names', (cfg.authorized_names || []).filter(x => x !== n))}
                            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                        </span>
                      ))}
                      {(cfg.authorized_names || []).length === 0 &&
                        <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>None — any person in the zone alerts.</span>}
                    </div>
                    <select value="" style={sel}
                      onChange={e => { const n = e.target.value; if (n && !(cfg.authorized_names || []).includes(n)) patch('authorized_names', [...(cfg.authorized_names || []), n]); }}>
                      <option value="">＋ Add authorised person…</option>
                      {knownNames.filter(n => !(cfg.authorized_names || []).includes(n)).map(n => <option key={n} value={n}>{n}</option>)}
                      {knownNames.length === 0 && <option value="" disabled>No enrolled persons — add them in Known Faces</option>}
                    </select>
                  </div>
                </Field>
                <Range label="Cooldown (s)" val={cfg.cooldown_secs} set={v => patch('cooldown_secs', v)} min={5} max={120} step={5} int />
                {mode === 'zone' && <Range label="Persist (frames)" val={cfg.persist_frames} set={v => patch('persist_frames', v)} min={1} max={10} step={1} int />}
                {mode === 'zone' && <Range label="Min dwell (s)" val={cfg.dwell_secs} set={v => patch('dwell_secs', v)} min={0} max={30} step={1} int />}
                {mode === 'boundary' && <Field label="Guarded side">
                  <select value={cfg.invert ? '1' : '0'} onChange={e => patch('invert', e.target.value === '1')} style={sel}>
                    <option value="0">Cross A → B</option><option value="1">Cross B → A (invert)</option>
                  </select>
                </Field>}
              </>}

              {mode === 'ignore' && <>
                <Field label="Ignore zones">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
                    {(cfg.zones || []).map((_, i) => (
                      <span key={i} className="ind-pill" style={{ display: 'inline-flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                        🚫 Ignore zone {i + 1}
                        <button onClick={() => removeIgnoreZone(i)}
                          style={{ background: 'none', border: 'none', color: 'var(--ind-bad, #e74c3c)', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>🗑</button>
                      </span>
                    ))}
                    {(cfg.zones || []).length === 0 &&
                      <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>None yet — draw an area above, then Finish zone.</span>}
                  </div>
                </Field>
              </>}
            </div>

            <div className="ind-acts" style={{ marginTop: 14 }}>
              <button className="ind-act pri" disabled={saving || !dirty} onClick={save}>{saving ? 'Saving…' : 'Save configuration'}</button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx3)', marginTop: 8 }}>
              {mode === 'counting'
                ? <>Counting runs on <b>{camName}</b>. Upload a trained model for real box/carton counting; the person base model counts people.</>
                : mode === 'ignore'
                  ? <>Draw areas where you want <b>no alerts</b> (a busy road, a neighbour's yard, a swaying tree). A person whose feet fall inside an ignore zone is dropped before any alert — so it <b>suppresses</b> alerts there. Draw several if needed.{/* No Telegram involved. — Telegram hidden from the UI */}</>
                  : <>Restricted-zone / boundary fires a full alert (snapshot + clip + siren{/* + Telegram — hidden from the UI */}). Authorised known faces are ignored so staff don't trip it. Alerts appear in the <b>Ops Console</b>.</>}
              {' '}Coordinates are stored normalised (resolution-safe).{' '}
              Live counts are on the <b>Object Intelligence Report</b> menu.
            </p>
          </div>
    </IndustrialLayout>
  );
}

// The label keeps its width (it used to shrink to one word per line next to a wide
// control); the controls take the rest and wrap under the label when there is no room.
function Field({ label, children }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
    <label style={{ width: 130, flexShrink: 0, fontSize: 12, color: 'var(--ind-tx2)' }}>{label}</label>
    <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {children}
    </div>
  </div>;
}
function Range({ label, val, set, min, max, step, int }) {
  return <Field label={label}>
    <input type="range" min={min} max={max} step={step} value={val}
      onChange={e => set(int ? parseInt(e.target.value, 10) : parseFloat(e.target.value))} style={{ flex: 1, maxWidth: 260 }} />
    <span style={mono}>{val}</span>
  </Field>;
}

function ModelUpload({ onUploaded }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async () => {
    if (!name.trim() || !file) { toast('Name and a .pt file are required.', 'err'); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('name', name.trim()); fd.append('description', desc); fd.append('file', file);
      const r = await apiFetch('/ui/models', { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'upload failed');
      setResult(d); setName(''); setDesc(''); setFile(null);
      toast(`Model "${d.name}" registered (${d.classes.length} classes).`, 'ok'); onUploaded?.();
    } catch (e) { toast(String(e.message || e), 'err'); }
    finally { setBusy(false); }
  };
  return (
    <div className="ind-card">
      <div className="ind-eye" style={{ marginBottom: 10 }}>Upload custom model (.pt)</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Model name" value={name} onChange={e => setName(e.target.value)} style={{ ...inp, width: 180 }} />
        <input placeholder="Description" value={desc} onChange={e => setDesc(e.target.value)} style={{ ...inp, width: 200 }} />
        <input type="file" accept=".pt" onChange={e => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
        <button className="ind-act pri" disabled={busy} onClick={upload} style={{ width: 'auto', padding: '0 14px' }}>{busy ? 'Validating…' : 'Upload'}</button>
      </div>
      {result && <div style={{ fontSize: 12, color: 'var(--ind-ok)', marginTop: 8 }}>✓ {result.name} — classes: {result.classes.map(c => c.name).join(', ')}</div>}
    </div>
  );
}

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12, maxWidth: '100%' };
const inp = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const mono = { fontFamily: 'var(--ind-mono)', fontSize: 12, color: 'var(--ind-accent)', width: 44, textAlign: 'right' };
